import Page from "@/components/Page"
import Spinner from "@/components/Spinner"
import { useSelfQuery } from "@/data/queries"
import { ROUTES, withQuery } from "@/routes"
import { useFinishOnboarding } from "@/util/hooks/useFinishOnboarding"
import { Navigate, useSearchParams } from "react-router"
import AddMedicationStep from "./steps/AddMedicationStep"
import DeviceReadyStep from "./steps/DeviceReadyStep"
import FindBarcodeStep from "./steps/FindBarcodeStep"
import MedicationQuestionStep from "./steps/MedicationQuestionStep"
import MedicationRemindersStep from "./steps/MedicationRemindersStep"
import ScanDeviceStep from "./steps/ScanDeviceStep"
import ShippingStep from "./steps/ShippingStep"
import WelcomeStep from "./steps/WelcomeStep"

const ONBOARDING_STEPS = [
  "welcome",
  "device-ready",
  "shipping",
  "find-barcode",
  "scan-device",
  "medication-question",
  "add-medication",
  "medication-reminders",
] as const
type OnboardingStep = (typeof ONBOARDING_STEPS)[number]

const isValidStep = (value: string | null): value is OnboardingStep =>
  ONBOARDING_STEPS.includes(value as OnboardingStep)

const stepUrl = (step: OnboardingStep) => withQuery(ROUTES.ONBOARDING, { step })

const OnboardingPage = () => {
  const [searchParams, setSearchParams] = useSearchParams()
  const step = searchParams.get("step")
  const { data: self } = useSelfQuery()
  const { finishOnboarding } = useFinishOnboarding()

  if (!isValidStep(step)) {
    return <Navigate to={stepUrl("welcome")} replace />
  }

  if (!self) {
    return (
      <Page showAppbar={false}>
        <div className="flex justify-center py-12">
          <Spinner />
        </div>
      </Page>
    )
  }

  // Steps replace each other in the history, so the browser's back never returns to scanning
  const goToStep = (next: OnboardingStep) => setSearchParams({ step: next }, { replace: true })

  const continueAfterLink = () => {
    if (self.patientAppState.profileFormFilled) {
      finishOnboarding()
    } else {
      goToStep("medication-question")
    }
  }

  switch (step) {
    case "welcome":
      return <WelcomeStep onNext={() => goToStep("device-ready")} />
    case "device-ready":
      return <DeviceReadyStep onNext={() => goToStep("shipping")} />
    case "shipping":
      return (
        <ShippingStep
          onNext={() =>
            goToStep(self.shouldScanBloodGlucoseDevice ? "find-barcode" : "medication-question")
          }
          onBack={() => goToStep("device-ready")}
        />
      )
    case "find-barcode":
      return (
        <FindBarcodeStep
          onNext={() => goToStep("scan-device")}
          onBack={() => goToStep("shipping")}
        />
      )
    case "scan-device":
      return (
        <ScanDeviceStep
          patientId={self.id}
          onLinked={continueAfterLink}
          onCheckAddress={() => goToStep("shipping")}
          onBack={() => goToStep("find-barcode")}
        />
      )
    case "medication-question":
      return (
        <MedicationQuestionStep
          onNext={() => goToStep("add-medication")}
          onBack={() => goToStep("shipping")}
        />
      )
    case "add-medication":
      return (
        <AddMedicationStep
          onNext={() => goToStep("medication-reminders")}
          onBack={() => goToStep("medication-question")}
        />
      )
    case "medication-reminders":
      return <MedicationRemindersStep onBack={() => goToStep("add-medication")} />
  }
}

export default OnboardingPage
